import { useEffect, useRef, useState } from "react";
import { Delete } from "lucide-react";
import { unlockAudio } from "../lib/scanner_audio";

/** 4-digit PIN entry with a big on-screen keypad (staff phone / customer phone). Calls onComplete(pin) at 4 digits. */
export function PinPad({ onComplete, error, disabled, accent = "#C8FF4D", ink = "#0B0D10", label, testid = "pin-pad" }) {
  const [pin, setPin] = useState("");
  const firedFor = useRef("");
  useEffect(() => {
    if (pin.length === 4 && firedFor.current !== pin) { firedFor.current = pin; onComplete(pin); }
  }, [pin, onComplete]);
  useEffect(() => { if (error) { setPin(""); firedFor.current = ""; } }, [error]);
  const push = (d) => { if (disabled || pin.length >= 4) return; unlockAudio(); if (navigator.vibrate) navigator.vibrate(6); setPin(pin + d); };
  const pop = () => setPin(pin.slice(0, -1));
  return (
    <div className="w-full max-w-[300px] mx-auto" data-testid={testid}>
      {label && <div className="text-center text-[11px] uppercase tracking-[0.2em] text-white/55 font-semibold mb-3">{label}</div>}
      <div className={`flex justify-center gap-3 mb-5 ${error ? "animate-[shake_0.35s_ease-in-out]" : ""}`} data-testid={`${testid}-dots`}>
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="h-4 w-4 rounded-full border-2 transition-all duration-150" style={{ borderColor: error ? "#FF7A3D" : accent, background: i < pin.length ? (error ? "#FF7A3D" : accent) : "transparent" }} />
        ))}
      </div>
      {error && <div className="text-center text-[#FF7A3D] text-[13px] font-medium mb-3" data-testid={`${testid}-error`}>{error}</div>}
      <div className="grid grid-cols-3 gap-2.5">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} type="button" onClick={() => push(d)} disabled={disabled} data-testid={`${testid}-key-${d}`}
            className="h-14 rounded-2xl bg-white/[0.08] border border-white/12 text-white font-display font-bold text-[20px] active:scale-[0.96] hover:bg-white/[0.14] transition disabled:opacity-40">{d}</button>
        ))}
        <span />
        <button type="button" onClick={() => push("0")} disabled={disabled} data-testid={`${testid}-key-0`}
          className="h-14 rounded-2xl bg-white/[0.08] border border-white/12 text-white font-display font-bold text-[20px] active:scale-[0.96] hover:bg-white/[0.14] transition disabled:opacity-40">0</button>
        <button type="button" onClick={pop} disabled={disabled || !pin} data-testid={`${testid}-back`}
          className="h-14 rounded-2xl grid place-items-center text-white/70 hover:text-white active:scale-[0.96] transition disabled:opacity-30"><Delete size={20} /></button>
      </div>
      <style>{`@keyframes shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-6px)}75%{transform:translateX(6px)}}`}</style>
    </div>
  );
}

export const PIN_MESSAGES = {
  pin_required: "Ask the customer to enter their 4-digit PIN.",
  pin_incorrect: "That PIN isn't right — try again.",
  pin_locked: "Too many attempts. This pass is locked for 15 minutes.",
  pin_not_set: "This customer hasn't set a PIN yet.",
};
